import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Briefcase,
  Users,
  CircleCheck,
  Sparkles,
  ClipboardCheck,
  Brain,
  Heart,
  X,
  Check,
} from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { useCreateJobOpeningMutation } from '../../../../utils/api_service/jobOpeningsApi';
import { RootState } from '../../../../utils/redux_store/store';
import { SubscriptionDetails } from '../../../../utils/types/admin_types';
import { toast } from '../../../../ui/toast';
import { JOB_OPENING_TYPE } from '../../Constants';
import {
  ALWAYS_HIDDEN_TYPES,
  DEFAULT_SELECTED_TYPES,
  DISABLED_TYPES,
} from '../constants';
import { ICreateJobDialogProps, IElementDefinition } from '../interfaces';

const getEnabledJobOpeningTypes = (
  subs: SubscriptionDetails | undefined,
): Set<JOB_OPENING_TYPE> => {
  const enabled = new Set<JOB_OPENING_TYPE>();
  if (!subs) return enabled;

  const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  if (isLicenseActive(subs.hierTalentBase)) enabled.add(JOB_OPENING_TYPE.APPLICANT_ANALYSIS);
  if (isLicenseActive(subs.hierTalentAutoClassify)) enabled.add(JOB_OPENING_TYPE.AUTO_SUITABILITY);
  if (isLicenseActive(subs.hierTalentAutoClassify)) enabled.add(JOB_OPENING_TYPE.AUTO_OUTSTANDING);
  if (isLicenseActive(subs.hierTalentJobPost)) enabled.add(JOB_OPENING_TYPE.JOB_POSTING);
  if (isLicenseActive(subs.hierTalentAutoAssessments)) enabled.add(JOB_OPENING_TYPE.AUTO_ASSESMENTS);
  if (isLicenseActive(subs.hierTalentSmartInterviews)) enabled.add(JOB_OPENING_TYPE.SMART_INTERVIEWS);
  if (isLicenseActive(subs.hierTalentPsychometricFaxtor)) enabled.add(JOB_OPENING_TYPE.PSYCHOMETRIC);
  if (isLicenseActive(subs.hierTalentWellness)) enabled.add(JOB_OPENING_TYPE.WELLNESS);

  return enabled;
};

const ICON_MAP: Record<JOB_OPENING_TYPE, React.ReactNode> = {
  [JOB_OPENING_TYPE.JOB_POSTING]: <Briefcase size={22} />,
  [JOB_OPENING_TYPE.APPLICANT_ANALYSIS]: <Users size={22} />,
  [JOB_OPENING_TYPE.AUTO_SUITABILITY]: <CircleCheck size={22} />,
  [JOB_OPENING_TYPE.AUTO_OUTSTANDING]: <Sparkles size={22} />,
  [JOB_OPENING_TYPE.AUTO_ASSESMENTS]: <ClipboardCheck size={22} />,
  [JOB_OPENING_TYPE.SMART_INTERVIEWS]: <Brain size={22} />,
  [JOB_OPENING_TYPE.PSYCHOMETRIC]: <Brain size={22} />,
  [JOB_OPENING_TYPE.WELLNESS]: <Heart size={22} />,
  [JOB_OPENING_TYPE.JOB_OPENING_DETAILS]: <Briefcase size={22} />,
  [JOB_OPENING_TYPE.ANALITICS]: <Briefcase size={22} />,
  [JOB_OPENING_TYPE.CCA]: <Briefcase size={22} />,
};

export const CreateJobDialog = ({ open, onClose, onSuccessClose }: ICreateJobDialogProps) => {
  const { t } = useTranslation(NS.adminPages);
  const history = useHistory();
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const [createJobOpening, { isLoading }] = useCreateJobOpeningMutation();

  const helpTextMap: Record<JOB_OPENING_TYPE, string> = {
    [JOB_OPENING_TYPE.JOB_POSTING]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.jobPosting),
    [JOB_OPENING_TYPE.APPLICANT_ANALYSIS]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.applicantAnalysis),
    [JOB_OPENING_TYPE.AUTO_SUITABILITY]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoSuitability),
    [JOB_OPENING_TYPE.AUTO_OUTSTANDING]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoOutstanding),
    [JOB_OPENING_TYPE.AUTO_ASSESMENTS]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoAssessments),
    [JOB_OPENING_TYPE.SMART_INTERVIEWS]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.smartInterviews),
    [JOB_OPENING_TYPE.CCA]: '',
    [JOB_OPENING_TYPE.JOB_OPENING_DETAILS]: '',
    [JOB_OPENING_TYPE.ANALITICS]: '',
    [JOB_OPENING_TYPE.PSYCHOMETRIC]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.psychometric),
    [JOB_OPENING_TYPE.WELLNESS]: t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.wellness),
  };

  const allElements: IElementDefinition[] = [
    { title: JOB_OPENING_TYPE.JOB_POSTING, label: 'JOB POSTING', icon: ICON_MAP[JOB_OPENING_TYPE.JOB_POSTING], helpText: helpTextMap[JOB_OPENING_TYPE.JOB_POSTING] },
    { title: JOB_OPENING_TYPE.APPLICANT_ANALYSIS, label: 'APPLICANT ANALYSIS', icon: ICON_MAP[JOB_OPENING_TYPE.APPLICANT_ANALYSIS], helpText: helpTextMap[JOB_OPENING_TYPE.APPLICANT_ANALYSIS] },
    { title: JOB_OPENING_TYPE.AUTO_SUITABILITY, label: 'AUTO-SUITABILITY', icon: ICON_MAP[JOB_OPENING_TYPE.AUTO_SUITABILITY], helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_SUITABILITY] },
    { title: JOB_OPENING_TYPE.AUTO_OUTSTANDING, label: 'AUTO-OUTSTANDING', icon: ICON_MAP[JOB_OPENING_TYPE.AUTO_OUTSTANDING], helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_OUTSTANDING] },
    { title: JOB_OPENING_TYPE.AUTO_ASSESMENTS, label: 'AUTO-ASSESSMENTS', icon: ICON_MAP[JOB_OPENING_TYPE.AUTO_ASSESMENTS], helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_ASSESMENTS] },
    { title: JOB_OPENING_TYPE.SMART_INTERVIEWS, label: 'SMART INTERVIEWS', icon: ICON_MAP[JOB_OPENING_TYPE.SMART_INTERVIEWS], helpText: helpTextMap[JOB_OPENING_TYPE.SMART_INTERVIEWS] },
    { title: JOB_OPENING_TYPE.PSYCHOMETRIC, label: 'PSYCHOMETRIC', icon: ICON_MAP[JOB_OPENING_TYPE.PSYCHOMETRIC], helpText: helpTextMap[JOB_OPENING_TYPE.PSYCHOMETRIC] },
    { title: JOB_OPENING_TYPE.WELLNESS, label: 'WELLNESS', icon: ICON_MAP[JOB_OPENING_TYPE.WELLNESS], helpText: helpTextMap[JOB_OPENING_TYPE.WELLNESS] },
  ];

  const enabledTypes = getEnabledJobOpeningTypes(subscriptions);
  const visibleElements = allElements.filter(({ title }) => enabledTypes.has(title as JOB_OPENING_TYPE));

  const [selectedTypes, setSelectedTypes] = useState<string[]>([...DEFAULT_SELECTED_TYPES]);

  const selectableElements = visibleElements.filter((item) => !DISABLED_TYPES.includes(item.title as JOB_OPENING_TYPE));
  const allSelected = selectableElements.length > 0 && selectableElements.every((item) => selectedTypes.includes(item.title));

  const handleToggle = (type: string) => {
    if (DISABLED_TYPES.includes(type as JOB_OPENING_TYPE)) return;
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedTypes([...DEFAULT_SELECTED_TYPES]);
    } else {
      const allSelectable = selectableElements.map((item) => item.title);
      setSelectedTypes([...DISABLED_TYPES, ...allSelectable]);
    }
  };

  const handleCreate = async () => {
    try {
      const result = await createJobOpening(selectedTypes).unwrap();
      history.push(`/job-openings/${result.id}`);
      if (onSuccessClose) onSuccessClose();
      else onClose();
    } catch {
      toast.error('Error Creating Job Opening', { position: 'bottom-center' });
    }
  };

  const visibleSelectedCount = selectedTypes.filter((tp) => !ALWAYS_HIDDEN_TYPES.includes(tp as JOB_OPENING_TYPE)).length;
  const visibleTotalCount = visibleElements.filter((i) => !ALWAYS_HIDDEN_TYPES.includes(i.title as JOB_OPENING_TYPE)).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'visible',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: '0 0 8px rgba(3,137,18,0.4)' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'primary.main', fontSize: '0.75rem' }}>
            CREATE NEW
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
          <X size={16} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {/* Select All + Counter */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={handleSelectAll}
            sx={{ cursor: 'pointer', '&:hover .select-label': { color: 'primary.main' } }}
          >
            <Checkbox checked={allSelected} size="small" color="primary" sx={{ p: 0 }} />
            <Typography className="select-label" variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', transition: 'color 0.2s' }}>
              {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.selectAll)}
            </Typography>
          </Stack>
          <Chip
            label={`${visibleSelectedCount} / ${visibleTotalCount} SELECTED`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em' }}
          />
        </Stack>

        {/* Grid of selectable elements */}
        <Grid container spacing={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 3, overflow: 'hidden', bgcolor: 'grey.50' }}>
          {visibleElements.map((item) => {
            const isSelected = selectedTypes.includes(item.title);
            const isDisabled = DISABLED_TYPES.includes(item.title as JOB_OPENING_TYPE);

            return (
              <Grid item xs={6} sm={3} key={item.title}>
                <Tooltip title={item.helpText} arrow placement="top">
                  <Box
                    onClick={() => handleToggle(item.title)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: { xs: 2, sm: 2.5 },
                      textAlign: 'center',
                      cursor: isDisabled ? 'default' : 'pointer',
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: 'grey.200',
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': !isDisabled ? { bgcolor: isSelected ? 'primary.100' : 'grey.50' } : undefined,
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'common.white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Check size={10} strokeWidth={3} />
                      </Box>
                    )}
                    <Box sx={{ color: isSelected ? 'primary.main' : 'text.secondary', transition: 'color 0.2s' }}>
                      {item.icon}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontSize: '0.6rem',
                        lineHeight: 1.3,
                        color: isSelected ? 'primary.dark' : 'text.secondary',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: 1, borderColor: 'grey.100' }}>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: 2.5, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.cancel)}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={isLoading || visibleSelectedCount === 0}
          sx={{
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            px: 3,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: '#ffffff',
            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
            '&:hover': {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.38)}`,
            },
          }}
        >
          {isLoading
            ? t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.creating)
            : t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.createButton)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
